/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Approval flows", description = "Hightouch supports change management in-app via approval flows. With this feature, changes to models and syncs require approval from a privileged user.", lastUpdatedDate = new Date(1671580800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Approval flows",
        description: "Hightouch supports change management in-app via approval flows. With this feature, changes to models and syncs require approval from a privileged user.",
        lastUpdatedDate: "Wed Dec 21 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Creating new resources",
          "depth": 0,
          "id": "creating-new-resources"
        }, {
          "value": "Editing existing resources",
          "depth": 0,
          "id": "editing-existing-resources"
        }, {
          "value": "Approving requests",
          "depth": 0,
          "id": "approving-requests"
        }, {
          "value": "Who can approve requests",
          "depth": 1,
          "id": "who-can-approve-requests"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      strong: "strong",
      img: "img",
      ul: "ul",
      li: "li",
      h2: "h2",
      em: "em",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      h3: "h3"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Approval flows",
        plural: true
      }), "\n", _jsxs(_components.p, {
        children: ["System administrators often want an extra layer of protection before changes to Hightouch ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " and ", _jsx(_components.a, {
          href: "/getting-started/concepts#syncs",
          children: "syncs"
        }), " are pushed into production. Hightouch supports this type of change management in-app via ", _jsx(_components.strong, {
          children: "approval flows"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspaces/permissions/approval-flow.png",
          alt: "Approval flow diagram"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Approval flows let you stage new resources (models and syncs) and changes to existing resources in ", _jsx(_components.strong, {
          children: "draft mode"
        }), " until an approver reviews them. This flow protects against accidents like:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Pushing duplicate leads into your CRM"
        }), "\n", _jsx(_components.li, {
          children: "Using an incorrect token when emailing users"
        }), "\n", _jsx(_components.li, {
          children: "Using an incorrect model for an advertising campaign"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsxs(_components.p, {
        children: ["Navigate to ", _jsx(_components.a, {
          href: "https://app.hightouch.com/settings",
          children: _jsx(_components.strong, {
            children: "Settings"
          })
        }), " in the right sidebar in Hightouch. In the ", _jsx(_components.strong, {
          children: "Workspace"
        }), " tab, toggle on ", _jsx(_components.strong, {
          children: "Require approvals"
        }), " and select ", _jsx(_components.strong, {
          children: "Save"
        }), "."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["By default, Workspace members aren't subject to approval flows. To opt them\nin, ", _jsx(_components.a, {
            href: "/workspace-management/rbac",
            children: "set their role"
          }), " to ", _jsx(_components.strong, {
            children: "Workspace Draft\nContributor"
          }), "."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Once enabled, approval flows require new or updated resources made by Workspace Draft Contributors to be approved by a ", _jsx(_components.a, {
          href: "#who-can-approve-requests",
          children: "privileged user"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "creating-new-resources",
        children: "Creating new resources"
      }), "\n", _jsx(_components.p, {
        children: "When you finish creating a new model or sync, you can choose from three options:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Publish now"
          }), " - ", _jsx(_components.em, {
            children: "Only available for privileged users"
          }), " - This option waives the approval flow and directly publishes changes."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Submit approval request"
          }), " - This signals that the new resource is ready for review. Choosing this option sends an email notification to the selected approver or approvers requesting them to approve the request in Hightouch. You can optionally leave a comment on the draft."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Save as draft"
          }), " - This leaves any changes in draft mode without notifying anyone. You can continue editing until you are ready for review, at which point you can submit your changes for approval using the ", _jsx(_components.strong, {
            children: "Submit approval request"
          }), " option."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If a sync depends on a model in draft mode, the approver must publish both resources before the sync can go live."
      }), "\n", _jsx(_components.h2, {
        id: "editing-existing-resources",
        children: "Editing existing resources"
      }), "\n", _jsxs(_components.p, {
        children: ["Editing existing models and syncs presents the same three options as ", _jsx(_components.a, {
          href: "/workspace-management/approval-flows#creating-new-resources",
          children: "creating new resources"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "These are the specific types of changes that require approval:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Resource"
            }), _jsx(_components.th, {
              children: "Changes requiring approval"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Model"
            }), _jsx(_components.td, {
              children: "Query, configuration, and column type changes"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Sync"
            }), _jsx(_components.td, {
              children: "Configuration and schedule changes"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "If a sync depends on a model that has unpublished changes, the sync refers to the columns of the draft model instead of the published model. The approver must publish both model and sync before the sync can go live."
      }), "\n", _jsx(_components.h2, {
        id: "approving-requests",
        children: "Approving requests"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "#who-can-approve-requests",
          children: "Approvers"
        }), " can view approval requests by selecting ", _jsx(_components.strong, {
          children: "View Request"
        }), " on the corresponding resource. When reviewing a request, the approver has three options:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Approve & publish"
          }), " - This makes a new resource or changes to an existing resource go live."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Delete draft"
          }), " - This deletes the underlying draft. New resources or drafted changes are discarded and can't be recovered."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Withdraw request"
          }), " - This removes the approval request but leaves the drafted resource or changes intact. Users can continue editing the draft."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Any privileged user with access to the resource can approve the request, even if they weren't the one notified to review the request."
      }), "\n", _jsx(_components.p, {
        children: "For changes to syncs, the approval request shows a comparison of changes to the sync configuration. For changes to models, the approval request shows changes to the model query."
      }), "\n", _jsx(_components.h3, {
        id: "who-can-approve-requests",
        children: "Who can approve requests"
      }), "\n", _jsxs(_components.p, {
        children: ["The ability to approve requests is a ", _jsx(_components.a, {
          href: "/workspace-management/rbac#actions",
          children: "specific permission"
        }), " granted to roles in Hightouch. By default, all roles ", _jsx(_components.a, {
          href: "/workspace-management/rbac#using-roles-with-approval-flows",
          children: "except Workspace Draft Contributor and Workspace Viewer"
        }), " can grant approval."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to grant approval permissions to other roles, you can build a ", _jsx(_components.a, {
          href: "/workspace-management/rbac#custom-roles",
          children: "custom role"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
